/**
 * SegmentTags — a twin's saved segments as small colored pills.
 *
 * Each pill wears its segment's plot palette color, from the room-wide slot
 * map the host builds with `getSegmentColorSlots` (a name missing from the
 * map gets the next free slot locally): a soft tinted fill, a hairline in the same hue and a solid dot, with
 * the label in the surface's normal foreground so it reads in light and dark.
 * Up to `max` pills show; the rest collapse into a "+N" pill whose title lists
 * every segment (screen readers hear the hidden names instead of "+N").
 * Nothing renders when there are no segments.
 */

import { CSSProperties } from 'react'

import { getSegmentColorSlots, segmentColor } from '@/lib/segments/segmentColor'
import { cn } from '@/lib/utils'

const DEFAULT_MAX = 1

interface SegmentTagsProps {
  names: Array<string>
  /** The room's segment-to-slot map, so a segment matches on every card. */
  colorSlots?: Map<string, number>
  /** Pills shown before the "+N" overflow pill. */
  max?: number
  /** `onImage` sits on a photo's dark foot: frosted white pills, the segment color stays on the dot. */
  tone?: 'surface' | 'onImage'
  className?: string
  'data-testid'?: string
}

const PILL_CLASS =
  'inline-flex h-5 min-w-0 items-center gap-1 rounded-full px-2 text-[11px] font-medium leading-none text-twin-chat-fg'

function getPillStyle(color: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in oklab, ${color} 16%, transparent)`,
    boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${color} 30%, transparent)`,
  }
}

export function SegmentTags(props: SegmentTagsProps) {
  const { names, colorSlots, max = DEFAULT_MAX, tone = 'surface', className, 'data-testid': testId = 'segment-tags' } = props

  const isOnImage = tone === 'onImage'

  const uniqueNames = Array.from(new Set(names.map((name) => name.trim()).filter(Boolean)))

  if (uniqueNames.length === 0) {
    return null
  }

  const slots = colorSlots ?? getSegmentColorSlots([uniqueNames])
  const shownNames = uniqueNames.slice(0, max)
  const hiddenCount = uniqueNames.length - shownNames.length

  return (
    <ul role="list" className={cn('flex max-w-full flex-nowrap gap-1', isOnImage ? 'justify-start' : 'justify-center', className)} data-testid={testId}>
      {shownNames.map((name, index) => {
        const color = segmentColor(slots.get(name) ?? index + 1)

        return (
          <li
            key={name}
            className={cn(PILL_CLASS, 'shrink', isOnImage && 'bg-white/20 text-white backdrop-blur-sm')}
            style={isOnImage ? undefined : getPillStyle(color)}
            title={name}
            data-segment-color={color}
            data-testid={`segment-tag-${index}`}
          >
            <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span className="truncate">{name}</span>
          </li>
        )
      })}

      {hiddenCount > 0 && (
        <li
          className={cn(
            PILL_CLASS,
            'shrink-0',
            isOnImage ? 'bg-white/20 text-white/90 backdrop-blur-sm' : 'bg-twin-chat-fg/[0.06] text-twin-chat-fg-muted',
          )}
          title={uniqueNames.join(', ')}
          data-testid="segment-tag-overflow"
        >
          <span aria-hidden="true">+{hiddenCount}</span>
          <span className="sr-only">{uniqueNames.slice(max).join(', ')}</span>
        </li>
      )}
    </ul>
  )
}
